import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ShoppingBag, Minus, Plus, Lock } from "lucide-react";

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "NSW",
    shipping_postcode: "",
    size: "",
    quantity: 1,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/merch/products");
        if (!cancelled) {
          setEnabled(Boolean(res.data?.enabled));
          setProducts(res.data?.products || []);
        }
      } catch {
        if (!cancelled) {
          setEnabled(false);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pickProduct = (p) => {
    setSelected(p);
    setForm((f) => ({ ...f, size: p.sizes?.[0] || "", quantity: 1 }));
    // Scroll the form into view on mobile
    setTimeout(() => {
      const el = document.getElementById("merch-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const unit = selected?.amount ?? 0;
  const total = (unit * (form.quantity || 1)).toFixed(2);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Pick an item first");
      return;
    }
    if (!form.size) {
      toast.error("Choose a size");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/merch/checkout", {
        product_id: selected.id,
        origin_url: window.location.origin,
        ...form,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Couldn't start checkout");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="shop"
      data-testid="merch-section"
      className="relative py-28 md:py-36 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#d4af37]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
                Fight Shop
              </span>
            </div>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9]">
              Rep The <br /> <span className="text-[#d4af37]">Colours.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-sm text-sm md:text-base">
            Gym-made gear. Ships Australia-wide. Secure checkout · Mastercard,
            Visa, Apple Pay &amp; Google Pay (AUD).
          </p>
        </div>

        {/* Coming-soon gate: shown until admin enables shop */}
        {loaded && !enabled && (
          <div
            data-testid="merch-coming-soon"
            className="border border-dashed border-[#d4af37]/40 bg-black/40 p-12 md:p-16 text-center"
          >
            <Lock className="mx-auto text-[#d4af37] mb-5" size={36} />
            <div className="font-heading text-3xl md:text-4xl uppercase">
              Shop opens soon.
            </div>
            <p className="mt-4 text-white/65 text-sm md:text-base max-w-md mx-auto leading-relaxed">
              We're finalising product photography and stock. Orders go live the
              moment our coaches confirm. Keep an eye on the Notice Board.
            </p>
          </div>
        )}

        {/* Product Grid */}
        {enabled && (
        <div
          data-testid="merch-grid"
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-14"
        >
          {products.map((p) => {
            const active = selected?.id === p.id;
            return (
              <button
                type="button"
                key={p.id}
                data-testid={`merch-product-${p.id}`}
                onClick={() => pickProduct(p)}
                className={`group text-left bg-[#050505] border transition-all ${
                  active
                    ? "border-[#d4af37]"
                    : "border-white/10 hover:border-[#d4af37]/60"
                }`}
              >
                <div className="relative aspect-square bg-black overflow-hidden flex items-center justify-center p-8">
                  <img
                    src={p.image}
                    alt={p.label}
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  {active && (
                    <span className="absolute top-3 right-3 bg-[#d4af37] text-black text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1">
                      Selected
                    </span>
                  )}
                </div>
                <div className="p-4 border-t border-white/10">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">
                    {p.description.split("·")[0].trim()}
                  </div>
                  <div className="mt-1 font-heading text-lg md:text-xl uppercase">
                    {p.label.replace("Full Circle MMA ", "")}
                  </div>
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="font-heading text-2xl text-[#d4af37]">
                      ${p.amount}
                    </span>
                    <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${active ? "text-[#d4af37]" : "text-white/40"}`}>
                      {active ? "Selected ✓" : "Shop"}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        )}

        {/* Order Form */}
        {enabled && selected && (
          <form
            id="merch-form"
            onSubmit={onSubmit}
            data-testid="merch-form"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-[#050505] border border-white/10 p-8 md:p-10"
          >
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-4">
                <ShoppingBag className="text-[#d4af37]" size={20} />
                <h3 className="font-heading text-2xl md:text-3xl uppercase">
                  {selected.label}
                </h3>
              </div>
              <p className="text-sm text-white/60">{selected.description}</p>

              {/* Size + Quantity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
                    Size
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.sizes.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`merch-size-${s}`}
                        onClick={() => setForm({ ...form, size: s })}
                        className={`px-4 py-2 border text-xs uppercase tracking-[0.2em] transition-colors ${
                          form.size === s
                            ? "bg-[#d4af37] text-black border-[#d4af37]"
                            : "border-white/15 text-white/80 hover:border-[#d4af37]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
                    Quantity
                  </Label>
                  <div className="mt-2 inline-flex items-center border border-white/15">
                    <button
                      type="button"
                      data-testid="merch-qty-minus"
                      onClick={() => setForm({ ...form, quantity: Math.max(1, form.quantity - 1) })}
                      className="w-11 h-11 flex items-center justify-center text-white/70 hover:text-[#d4af37]"
                      aria-label="Decrease"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-12 text-center font-heading text-lg">
                      {form.quantity}
                    </span>
                    <button
                      type="button"
                      data-testid="merch-qty-plus"
                      onClick={() => setForm({ ...form, quantity: Math.min(10, form.quantity + 1) })}
                      className="w-11 h-11 flex items-center justify-center text-white/70 hover:text-[#d4af37]"
                      aria-label="Increase"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Customer details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-white/10">
                <MerchField label="Full Name">
                  <Input data-testid="merch-name" required value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                </MerchField>
                <MerchField label="Email">
                  <Input data-testid="merch-email" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                </MerchField>
                <MerchField label="Phone">
                  <Input data-testid="merch-phone" required value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                </MerchField>
                <MerchField label="Postcode">
                  <Input data-testid="merch-postcode" required value={form.shipping_postcode}
                    onChange={(e) => setForm({ ...form, shipping_postcode: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                </MerchField>
                <div className="md:col-span-2">
                  <MerchField label="Shipping Address">
                    <Input data-testid="merch-address" required value={form.shipping_address}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                      className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                  </MerchField>
                </div>
                <MerchField label="City / Suburb">
                  <Input data-testid="merch-city" required value={form.shipping_city}
                    onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                </MerchField>
                <MerchField label="State">
                  <Input data-testid="merch-state" required value={form.shipping_state}
                    onChange={(e) => setForm({ ...form, shipping_state: e.target.value })}
                    className="bg-transparent border-white/15 rounded-none h-11 focus-visible:ring-[#d4af37]" />
                </MerchField>
              </div>
            </div>

            {/* Order summary */}
            <aside className="lg:col-span-5">
              <div className="bg-[#0a0a0a] border border-white/10 p-7 sticky top-24">
                <div className="text-xs uppercase tracking-[0.25em] text-[#d4af37] font-bold">
                  Order Summary
                </div>
                <div className="mt-4 border-t border-white/10 pt-4 space-y-3">
                  <Row label="Item">{selected.label}</Row>
                  <Row label="Size">{form.size || "—"}</Row>
                  <Row label="Quantity">× {form.quantity}</Row>
                  <Row label="Unit Price">${unit.toFixed(2)} AUD</Row>
                </div>
                <div className="mt-6 border-t border-white/10 pt-6 flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-[0.25em] text-white/60">
                    Total
                  </span>
                  <span className="font-heading text-4xl text-[#d4af37]">
                    ${total}
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="merch-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#d4af37] text-black text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#f1c40f] transition-colors disabled:opacity-50"
                >
                  <ShoppingBag size={14} />
                  {loading ? "Redirecting to Stripe…" : "Checkout"}
                </button>
                <div className="mt-4 text-[11px] text-white/50 leading-relaxed">
                  Ships Australia-wide within 5–7 business days. Secure checkout —
                  Mastercard, Visa, Apple Pay, Google Pay.
                </div>
              </div>
            </aside>
          </form>
        )}
      </div>
    </section>
  );
}

function MerchField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-white/60">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/50 uppercase tracking-[0.15em] text-[11px]">
        {label}
      </span>
      <span className="text-white/90 text-right max-w-[60%] truncate">
        {children}
      </span>
    </div>
  );
}
